import { Transform } from "./Transform.js";
import { findVisualCenter, cropTransparentImage } from "./VisualUtils.js";

export class Layer {
    constructor(image, id) {
        this.id = id;
        this.sourceImage = image;
        this.transform = new Transform();
        this.canvas = null;
        this.visible = true;
        this.blendMode = "source-over";
        this._visualCenter = null;
        this.visualCenter = null; // Public getter for InteractiveCanvas
    }

    render(width, height) {
        if (!this.sourceImage) {
            return null;
        }

        // Use the faster _render for static layers (transform = null) or expanded canvases
        // (which handle transforms at the Compositor level)
        if (!this.transform || (this.sourceImage instanceof HTMLCanvasElement && this.sourceImage._isExpandedCanvas)) {
            return this._render(width, height);
        } else {
            return this._renderWithTransform(width, height);
        }
    }
    
    _render(width, height) {
        // For sourceImages that are expanded canvases, use them directly to preserve metadata
        // This is critical for maintaining the viewport offset information needed for proper positioning
        if (this.sourceImage instanceof HTMLCanvasElement && this.sourceImage._isExpandedCanvas) {
            this.canvas = this.sourceImage;
            return this.canvas;
        }
        
        // For sourceImages that are already canvases with the right dimensions,
        // use them directly to preserve any metadata
        if (this.sourceImage instanceof HTMLCanvasElement && 
            this.sourceImage.width === width && 
            this.sourceImage.height === height) {
            this.canvas = this.sourceImage;
            return this.canvas;
        }
        
        // Otherwise create or resize canvas as needed
        if (!this.canvas || this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas = document.createElement("canvas");
            this.canvas.width = width;
            this.canvas.height = height;
        }

        const ctx = this.canvas.getContext("2d");
        ctx.clearRect(0, 0, width, height);

        // Draw the source image at its original position
        // Note: If sourceImage is from ContentRenderer, it's already positioned correctly
        ctx.drawImage(this.sourceImage, 0, 0);

        return this.canvas;
    }

    _renderWithTransform(width, height) {
        // Bakes the current transform into this.canvas.
        // NOTE: SurfaceModeRenderer.drawUserImageWithTransforms / drawAltUserImageWithTransforms
        // read from sourceImage (not canvas) to avoid double-applying the transform.
        // Create or resize canvas as needed
        if (!this.canvas || this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas = document.createElement("canvas");
            this.canvas.width = width;
            this.canvas.height = height;
        }

        const ctx = this.canvas.getContext("2d");
        ctx.clearRect(0, 0, width, height);
        
        // Calculate the visual center if not already done
        if (!this._visualCenter) {
            this._visualCenter = this.calculateVisualCenter();
            // Also set the public property for InteractiveCanvas
            this.visualCenter = {
                x: this._visualCenter[0],
                y: this._visualCenter[1]
            };
        }

        // Save context state
        ctx.save();
        
        // Apply transformations
        this.transform.apply(ctx, this._visualCenter);
        
        // Draw the source image at its original position
        // Note: If sourceImage is from ContentRenderer, it's already positioned correctly
        // If sourceImage is a raw image, it will be drawn at top-left (0,0)
        ctx.drawImage(this.sourceImage, 0, 0);
        
        // Restore context state
        ctx.restore();

        return this.canvas;
    }
    
    applyMask(maskLayer) {
        if (!this.canvas || !maskLayer || !maskLayer.canvas) {
            return;
        }

        const ctx = this.canvas.getContext("2d");
        
        // Apply the mask using "destination-in" compositing
        // This keeps only the parts of the destination that overlap with non-transparent parts of the source
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(maskLayer.canvas, 0, 0);
        
        // Reset compositing mode
        ctx.globalCompositeOperation = "source-over";
    }

    calculateVisualCenter() {
        if (!this.sourceImage) {
            return [0, 0];
        }

        // Create a temporary canvas with the source image
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = this.sourceImage.width;
        tempCanvas.height = this.sourceImage.height;
        const tempCtx = tempCanvas.getContext("2d");
        tempCtx.drawImage(this.sourceImage, 0, 0);

        // Find the visual center (center of non-transparent pixels)
        return findVisualCenter(tempCanvas);
    }
}